import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../api/axios";
import loadStatus from "../util/loadStatus";

export const getNations = createAsyncThunk(
  "nations/getNations",
  async (reqData, { rejectWithValue, getState }) => {
    try {
      const state = getState().nations;
      if (state.count.getNations <= 1) {
        const res = await axios.get("/nations", { params: reqData });
        return res.data;
      }
      return state;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error);
    }
  }
);

export const getNation = createAsyncThunk(
  "nations/getNation",
  async (reqData, { rejectWithValue, getState }) => {
    try {
      const state = getState().nations;
      if (state.count.getNation <= 1) {
        const res = await axios.get("/nations/" + reqData);
        return res.data;
      }
      return state.selected;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error);
    }
  }
);

export const getOrgProjectReports = createAsyncThunk(
  "projects/getOrgProjectReports",
  async (reqData, { rejectWithValue, getState }) => {
    try {
      const state = getState().nations;
      if (state.count.getOrgProjectReports <= 1) {
        const res = await axios.get("/project-reports");

        return res.data;
      }
      return state.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data.message);
      }

      return rejectWithValue(error.message);
    }
  }
);

export const addNation = createAsyncThunk(
  "nations/addNation",
  async (reqData, { rejectWithValue }) => {
    try {
      const res = await axios.post("/add-nation", reqData);
      return res.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error);
    }
  }
);

export const editNation = createAsyncThunk(
  "nations/editNation",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await axios.put("/edit-nation/" + id, data);
      return res.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error);
    }
  }
);

export const deleteNation = createAsyncThunk(
  "nations/deleteNation",
  async ({ id, reqData }, { rejectWithValue }) => {
    try {
      const res = await axios.delete("/delete-nation/" + id, {
        params: reqData,
      });
      return res.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error);
    }
  }
);

const nationsSlice = createSlice({
  name: "nations",
  initialState: {
    total: 0,
    data: [],
    selected: {},
    loading: false,
    error: null,
    load: {
      getNations: false,
      getNation: false,
      getOrgProjectReports: false,
      getDonor: false,
      addNation: false,
      editNation: false,
      deleteNation: false,
    },
    count: {
      getNations: 0,
      getNation: 0,
      getDonor: 0,
      getOrgProjectReports: 0,
    },
  },
  reducers: {
    setNations: (state, action) => {
      state.data = action.payload;
    },
    setSelectedOrg: (state, action) => {
      state.selected = action.payload;
    },
    setSelectedNation: (state, action) => {
      state.selected = action.payload;
    },
    unsetSelectedNation: (state) => {
      state.selected = {};
    },
    unsetNations: (state) => {
      state.data = [];
      state.selected = {};
    },
  },
  extraReducers: (builder) => {
    builder
      //Get all nations
      .addCase(getNations.pending, (state) => {
        state.load.getNations = loadStatus.loading;
        state.error = null;
        state.count.getNations = ++state.count.getNations;
      })
      .addCase(getNations.fulfilled, (state, action) => {
        state.load.getNations = loadStatus.success;
        state.data = action.payload.data;
        state.total = action.payload.count;
        state.count.getNations = 0;
      })
      .addCase(getNations.rejected, (state, action) => {
        state.load.getNations = loadStatus.error;
        state.error = action.payload;
        state.count.getNations = 0;
      })
      //Get Nation
      .addCase(getNation.pending, (state) => {
        state.load.getNation = loadStatus.loading;
        state.error = null;
        state.count.getNation = ++state.count.getNation;
      })
      .addCase(getNation.fulfilled, (state, action) => {
        state.load.getNation = loadStatus.success;
        state.selected = action.payload;
        state.count.getNation = 0;
      })
      .addCase(getNation.rejected, (state, action) => {
        state.load.getNation = loadStatus.error;
        state.error = action.payload;
        state.count.getNation = 0;
      })
      //Get all Projects Reports
      .addCase(getOrgProjectReports.pending, (state) => {
        state.load.getOrgProjectReports = loadStatus.loading;
        state.error = null;
        state.count.getOrgProjectReports = ++state.count.getOrgProjectReports;
      })
      .addCase(getOrgProjectReports.fulfilled, (state, action) => {
        state.load.getOrgProjectReports = loadStatus.success;
        state.data = action.payload;
        state.count.getOrgProjectReports = 0;
      })
      .addCase(getOrgProjectReports.rejected, (state, action) => {
        state.load.getOrgProjectReports = loadStatus.error;
        state.error = action.error;
        state.count.getOrgProjectReports = 0;
      })
      //Add Organization
      .addCase(addNation.pending, (state) => {
        state.load.addNation = loadStatus.loading;
        state.error = null;
      })
      .addCase(addNation.fulfilled, (state, action) => {
        state.load.addNation = loadStatus.success;
        state.data.unshift(action.payload);
      })
      .addCase(addNation.rejected, (state, action) => {
        state.load.addNation = loadStatus.error;
        state.error = action.payload;
      })
      //Update Organization
      .addCase(editNation.pending, (state) => {
        state.load.editNation = loadStatus.loading;
        state.error = null;
      })
      .addCase(editNation.fulfilled, (state, action) => {
        state.load.editNation = loadStatus.success;
        state.selected = action.payload;
        const index = state.data.findIndex(
          (item) => (item.id = action.payload.id)
        );
        if (index !== -1) {
          state.data[index] = action.payload;
        }
      })
      .addCase(editNation.rejected, (state, action) => {
        state.load.editNation = loadStatus.error;
        state.error = action.payload;
      })
      //Delete Organization
      .addCase(deleteNation.pending, (state) => {
        state.load.deleteNation = loadStatus.loading;
        state.error = null;
      })
      .addCase(deleteNation.fulfilled, (state, action) => {
        state.load.deleteNation = loadStatus.success;
        state.data.filter((item) => item.id !== action.meta.arg.id);
      })
      .addCase(deleteNation.rejected, (state, action) => {
        state.load.deleteNation = loadStatus.error;
        state.error = action.payload;
      });
  },
});

export const {
  setNations,
  setSelectedOrg,
  unsetNations,
  setSelectedNation,
  unsetSelectedNation,
} = nationsSlice.actions;
export default nationsSlice.reducer;
